import * as React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, type LucideIcon } from 'lucide-react';

import { useToast as useBaseToast, toast as baseToast } from '@/components/ui/use-toast';

export type ToastKind = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  kind?: ToastKind;
  title: string;
  body?: string;
  duration?: number;
}

interface KindStyle {
  bg: string;
  border: string;
  icon: LucideIcon;
  iconColor: string;
  titleColor: string;
}

// Per design spec (04-global-components.html §2 + README §4c).
// Same colour family as AlertBanner. Tokens used where a CSS var exists;
// the remaining semantic tints are the exact values from the handoff.
export const TOAST_KIND_STYLES: Record<ToastKind, KindStyle> = {
  success: {
    bg: '#F0FDF4',
    border: '#BBF7D0',
    icon: CheckCircle,
    iconColor: 'var(--status-active)',
    titleColor: '#14532D',
  },
  error: {
    bg: '#FEF2F2',
    border: '#FECACA',
    icon: XCircle,
    iconColor: 'var(--status-expired)',
    titleColor: '#7F1D1D',
  },
  warning: {
    bg: '#FFFBEB',
    border: '#FDE68A',
    icon: AlertTriangle,
    iconColor: '#D97706',
    titleColor: '#78350F',
  },
  info: {
    bg: 'var(--accent-light)',
    border: 'var(--accent-muted)',
    icon: Info,
    iconColor: 'var(--accent)',
    titleColor: '#1E1B4B',
  },
};

// success/info → 4000ms; error/warning → 8000ms (operator needs time to read).
export function defaultDuration(kind: ToastKind): number {
  return kind === 'error' || kind === 'warning' ? 8000 : 4000;
}

// Build the radix-toast payload from our semantic options. Carries the
// kind-based skin via `style` on the toast Root and renders the leading icon
// inside the title node so no ui/ file needs changing.
export function buildToast(opts: ToastOptions): Parameters<typeof baseToast>[0] {
  const kind = opts.kind ?? 'info';
  const s = TOAST_KIND_STYLES[kind];
  const IconCmp = s.icon;
  return {
    duration: opts.duration ?? defaultDuration(kind),
    style: {
      background: s.bg,
      border: `1px solid ${s.border}`,
      color: s.titleColor,
    } as React.CSSProperties,
    title: (
      <span
        data-kind={kind}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: s.titleColor }}
      >
        <IconCmp size={16} color={s.iconColor} style={{ flexShrink: 0 }} />
        {opts.title}
      </span>
    ),
    description: opts.body,
  };
}

// Imperative variant (usable outside React render).
export function toast(opts: ToastOptions) {
  return baseToast(buildToast(opts));
}

// Hook variant. Re-exports the base toast state (so a <Toaster/> can render)
// and exposes the semantic toast({ kind, title, body, duration? }) helper.
export function useToast() {
  const base = useBaseToast();
  const semanticToast = React.useCallback((opts: ToastOptions) => base.toast(buildToast(opts)), [base]);
  return {
    ...base,
    toast: semanticToast,
  };
}

export default useToast;
